import { monthsToYears } from 'date-fns';
import { DataListItem } from '@brighte/form-components';
import {
  CategoryComponent,
  FinancialProduct,
  ModifiedApplicationQuote,
  RelatedReferralWithSummary,
  RepaymentOption,
  RepaymentTermInput,
} from '@graphql/index';
import {
  BGL_MAX_INTEREST_RATE,
  BRIGHTE_GREEN_LOAN,
  FINANCE_PURCHASE,
  PAYMENT_DELAY_DAYS,
  deSlug,
  formatDollars,
  isFloat,
  productIds,
} from '@utils';

const { CATEGORY_COMPONENTS, DESCRIPTION, DESCRIPTION_NO_TERM, DESCRIPTION_BRIGHTE_PAY } = FINANCE_PURCHASE;

type UnitsMapType = Record<
  keyof Pick<
    CategoryComponent,
    'pvPanelCapacity' | 'inverterCapacity' | 'heatingCoolingSystemCapacity' | 'batteryCapacity' | 'hwhpTankCapacity'
  >,
  string
>;

const unitsMap: UnitsMapType = {
  pvPanelCapacity: 'kW',
  inverterCapacity: 'kW',
  heatingCoolingSystemCapacity: 'kW',
  batteryCapacity: 'kWh',
  hwhpTankCapacity: 'litre',
};

export const getUnits = (categoryComponent: keyof CategoryComponent) => unitsMap[categoryComponent] || '';

export const formatPurchaseItem = ({ value, unit = '', manufacturerSlug = '' }) => {
  if (typeof value === 'object') {
    let purchaseItem = '';
    Object.entries(value).forEach(([key, value]) => {
      if (value) {
        purchaseItem += `${CATEGORY_COMPONENTS[key]}, `;
      }
    });
    purchaseItem = purchaseItem.slice(0, -2);
    return purchaseItem;
  }

  const manufacturer = manufacturerSlug ? `${deSlug(manufacturerSlug)} ${String.fromCodePoint(8226)} ` : '';

  return value ? `${manufacturer}${value} ${unit}` : value;
};

export const getRepaymentDetails = (
  repaymentTerm: string | number,
  quote: Partial<ModifiedApplicationQuote> | Partial<RelatedReferralWithSummary>,
): RepaymentTermInput | undefined => {
  const selectedTerm = typeof repaymentTerm === 'number' ? repaymentTerm : Number(repaymentTerm);

  if (!quote) return;

  const { repaymentOptions } = quote;

  const selectedRepaymentTerm = repaymentOptions?.find(item => item?.termMonths === selectedTerm);

  if (!selectedTerm || !selectedRepaymentTerm) return;

  // Returns only the properties of RepaymentTermInput
  return {
    term: selectedRepaymentTerm.termMonths / 12,
    termInMonths: selectedRepaymentTerm.termMonths,
    amount: selectedRepaymentTerm.repaymentAmount,
    interestCycle: selectedRepaymentTerm.interestCycle,
    interestRate: selectedRepaymentTerm.interestRate,
    totalInterest: selectedRepaymentTerm.totalInterest,
    totalPayments: selectedRepaymentTerm.totalPayments,
    paymentCycle: selectedRepaymentTerm.paymentCycle,
    termNumberOfPayments: selectedRepaymentTerm.numberOfPayments,
  };
};

export const getDescriptionByFinancialProduct = (financialProduct?: FinancialProduct): string => {
  if (financialProduct?.id === productIds.FLEX || financialProduct?.id === productIds.ELECTRIFY_2515) {
    return DESCRIPTION_NO_TERM;
  }

  if (financialProduct?.id === productIds.BRIGHTE_PAY) {
    return DESCRIPTION_BRIGHTE_PAY;
  }

  return DESCRIPTION;
};

export const withSingleRepaymentTerm = (repaymentOptions): boolean => {
  return repaymentOptions && repaymentOptions.length === 1;
};

export const getTermLabel = (
  repayments: RepaymentOption[],
  term?: RepaymentTermInput['termInMonths'],
): string | null => {
  if (!term) return null;

  const hasFloat = repayments.some(({ termMonths }: RepaymentOption) =>
    termMonths ? isFloat(termMonths / 12) : false,
  );

  return hasFloat ? `${term} months` : `${monthsToYears(term)} years`;
};

export const getAnnualTermRates = (
  selectedTerm: string | number | undefined,
  quote?: Pick<ModifiedApplicationQuote, 'financialProduct' | 'productConfiguration' | 'repaymentOptions'>,
) => {
  // selects the highest interest rate from the term options
  const highestInterestRate =
    quote?.repaymentOptions?.reduce(
      (highest, curr) => (Number(curr.interestRate) > highest ? Number(curr.interestRate) : highest),
      0,
    ) ?? 0;

  // get the interest rate for the selected term
  const interestRate =
    quote?.repaymentOptions?.find(term => term.termMonths === Number(selectedTerm))?.interestRate ??
    quote?.productConfiguration?.interestRate ??
    0;

  const max = highestInterestRate + BGL_MAX_INTEREST_RATE;

  return [interestRate, max];
};

export function getRepaymentDueField(quote): DataListItem {
  const {
    financialProduct: { id: financialProductId },
  } = quote;

  const promoDelayDays = PAYMENT_DELAY_DAYS(quote);
  switch (financialProductId) {
    case productIds.FLEX:
      return {
        label: `Single payment of ${formatDollars(quote.financedAmount, {
          zeroValue: '$0.00',
          hideZeroCents: true,
        })} due in ${promoDelayDays} days after installation`,
      };
    default:
      return {
        label: `First repayment due ${promoDelayDays} days after installation`,
      };
  }
}

export const financialProductLabel = (financialProduct: FinancialProduct | undefined) =>
  financialProduct?.id === productIds.ELECTRIFY_2515 ? BRIGHTE_GREEN_LOAN : financialProduct?.name;

type RepaymentOptions = {
  total: number;
  repayments: number;
  accountKeepingFee: number;
};

export const fortnightlyRepaymentAmount = (options: RepaymentOptions): string => {
  const { repayments, total, accountKeepingFee } = options;

  const decimals = 2;
  const calculated = total / repayments + accountKeepingFee * 2;
  const rounded = Math.ceil(calculated * 100) / 100;

  return rounded.toFixed(decimals).replace('.00', '');
};
